import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import {
  PlusIcon,
  XMarkIcon,
  AcademicCapIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { trainingApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import FeatureGate from '../components/FeatureGate';
import PermissionGate from '../components/PermissionGate';
import {
  PageHeader,
  StatusBadge,
  DataTable,
  StatCard,
} from '../components/ui';
import type { Column, BadgeVariant } from '../components/ui';

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_TABS = [
  { key: 'ALL', label: 'All' },
  { key: 'ACTIVE', label: 'Active' },
  { key: 'DRAFT', label: 'Draft' },
  { key: 'ARCHIVED', label: 'Archived' },
];

const STATUS_BADGE_MAP: Record<string, BadgeVariant> = {
  ACTIVE: 'success',
  DRAFT: 'neutral',
  ARCHIVED: 'neutral',
};

const CATEGORIES = [
  { value: 'BSA_AML', label: 'BSA/AML' },
  { value: 'FAIR_LENDING', label: 'Fair Lending' },
  { value: 'MARKETING_COMPLIANCE', label: 'Marketing Compliance' },
  { value: 'HMDA', label: 'HMDA' },
  { value: 'PRIVACY', label: 'Privacy' },
  { value: 'CYBERSECURITY', label: 'Cybersecurity' },
  { value: 'VENDOR_MANAGEMENT', label: 'Vendor Management' },
  { value: 'GENERAL_COMPLIANCE', label: 'General Compliance' },
  { value: 'NEW_HIRE', label: 'New Hire' },
  { value: 'BOARD', label: 'Board' },
  { value: 'OTHER', label: 'Other' },
];

const FREQUENCIES = [
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'SEMI_ANNUAL', label: 'Semi-Annual' },
  { value: 'ANNUAL', label: 'Annual' },
  { value: 'ONE_TIME', label: 'One-Time' },
  { value: 'CUSTOM', label: 'Custom' },
];

const CATEGORY_LABEL: Record<string, string> = Object.fromEntries(
  CATEGORIES.map((c) => [c.value, c.label]),
);

const FREQUENCY_LABEL: Record<string, string> = Object.fromEntries(
  FREQUENCIES.map((f) => [f.value, f.label]),
);

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function TrainingManagementPage() {
  const { teamId } = useTeamId();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [showCreate, setShowCreate] = useState(false);

  const statusFilter = STATUS_TABS[activeTab].key;

  // Dashboard stats
  const { data: dashData } = useQuery({
    queryKey: ['trainingDashboard', teamId],
    queryFn: () => trainingApi.dashboard(teamId!),
    enabled: !!teamId,
  });
  const dash = dashData?.data;

  // Programs list
  const { data: listData, isLoading } = useQuery({
    queryKey: ['trainingPrograms', teamId, statusFilter],
    queryFn: () =>
      trainingApi.list(teamId!, {
        status: statusFilter === 'ALL' ? undefined : statusFilter,
      }),
    enabled: !!teamId,
  });
  const programs = listData?.data?.programs || [];

  // ── Columns ───────────────────────────────────────────────────────────

  const columns: Column<any>[] = [
    {
      key: 'title',
      header: 'Program',
      render: (row: any) => (
        <div>
          <div className="font-medium text-gray-900">{row.title}</div>
          {row.provider && (
            <div className="text-xs text-gray-500">
              {row.provider}
              {row.providerUrl && (
                <a
                  href={row.providerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-1 inline-flex items-center text-blue-600 hover:text-blue-800"
                >
                  <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                </a>
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      render: (row: any) => (
        <span className="text-sm font-medium text-gray-900">
          {CATEGORY_LABEL[row.category] || row.category}
        </span>
      ),
    },
    {
      key: 'frequency',
      header: 'Frequency',
      render: (row: any) => (
        <span className="text-sm text-gray-900">
          {FREQUENCY_LABEL[row.frequency] || row.frequency}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row: any) => (
        <StatusBadge variant={STATUS_BADGE_MAP[row.status] || 'neutral'}>
          {row.status}
        </StatusBadge>
      ),
    },
    {
      key: '_count',
      header: 'Tracked',
      render: (row: any) => (
        <span className="text-sm text-gray-600">
          {row._count?.completions ?? 0} records
        </span>
      ),
    },
    {
      key: 'durationMinutes',
      header: 'Duration',
      render: (row: any) =>
        row.durationMinutes ? (
          <span className="text-sm text-gray-600">{row.durationMinutes} min</span>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        ),
    },
  ];

  return (
    <FeatureGate feature="training_management">
      <div className="space-y-6">
        <PageHeader
          title="Training Management"
          description="Track compliance training requirements, completions, and certificates for regulatory exam readiness."
          actions={
            <PermissionGate teamId={teamId} permission="compliance.write">
              <button
                onClick={() => setShowCreate(true)}
                className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-teal-700"
              >
                <PlusIcon className="h-4 w-4" />
                New Program
              </button>
            </PermissionGate>
          }
        />

        {/* Dashboard Stats */}
        {dash && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Total Programs"
              value={dash.totalPrograms}
              icon={AcademicCapIcon}
            />
            <StatCard
              label="Active Programs"
              value={dash.activePrograms}
              icon={CheckCircleIcon}
            />
            <StatCard
              label="Overdue"
              value={dash.completionStats?.overdue ?? 0}
              icon={ExclamationTriangleIcon}
            />
            <StatCard
              label="In Progress"
              value={dash.completionStats?.inProgress ?? 0}
              icon={ClockIcon}
            />
          </div>
        )}

        {/* Status Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {STATUS_TABS.map((tab, idx) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(idx)}
                className={clsx(
                  'whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium',
                  activeTab === idx
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                )}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Programs Table */}
        <DataTable
          columns={columns}
          data={programs}
          isLoading={isLoading}
          emptyTitle="No training programs"
          emptyDescription="Create a training program to start tracking compliance training requirements and completions."
          keyExtractor={(row: any) => row.id}
          onRowClick={(row: any) => navigate(`/training/${row.id}`)}
        />

        {/* Create Dialog */}
        <CreateProgramDialog
          open={showCreate}
          onClose={() => setShowCreate(false)}
          teamId={teamId!}
          queryClient={queryClient}
        />
      </div>
    </FeatureGate>
  );
}

// ─── Create Program Dialog ────────────────────────────────────────────────────

function CreateProgramDialog({
  open,
  onClose,
  teamId,
  queryClient,
}: {
  open: boolean;
  onClose: () => void;
  teamId: string;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: 'GENERAL_COMPLIANCE',
    frequency: 'ANNUAL',
    provider: '',
    providerUrl: '',
    durationMinutes: '',
    regulatoryBasis: '',
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => trainingApi.create(teamId, data),
    onSuccess: () => {
      toast.success('Training program created');
      queryClient.invalidateQueries({ queryKey: ['trainingPrograms'] });
      queryClient.invalidateQueries({ queryKey: ['trainingDashboard'] });
      onClose();
      setForm({
        title: '',
        description: '',
        category: 'GENERAL_COMPLIANCE',
        frequency: 'ANNUAL',
        provider: '',
        providerUrl: '',
        durationMinutes: '',
        regulatoryBasis: '',
      });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Failed to create program');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate({
      ...form,
      durationMinutes: form.durationMinutes ? parseInt(form.durationMinutes, 10) : undefined,
      providerUrl: form.providerUrl || undefined,
    });
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    New Training Program
                  </Dialog.Title>
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title *
                    </label>
                    <input
                      type="text"
                      required
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      placeholder="e.g., Annual BSA/AML Training"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      rows={2}
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category
                      </label>
                      <select
                        value={form.category}
                        onChange={(e) => setForm({ ...form, category: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      >
                        {CATEGORIES.map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Frequency
                      </label>
                      <select
                        value={form.frequency}
                        onChange={(e) => setForm({ ...form, frequency: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      >
                        {FREQUENCIES.map((f) => (
                          <option key={f.value} value={f.value}>
                            {f.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Provider
                      </label>
                      <input
                        type="text"
                        value={form.provider}
                        onChange={(e) => setForm({ ...form, provider: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                        placeholder="e.g., ABA, BankersEdge"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Duration (min)
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={form.durationMinutes}
                        onChange={(e) => setForm({ ...form, durationMinutes: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Regulatory Basis
                    </label>
                    <input
                      type="text"
                      value={form.regulatoryBasis}
                      onChange={(e) => setForm({ ...form, regulatoryBasis: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      placeholder="e.g., BSA/AML — 31 CFR 1020.210"
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={createMutation.isPending}
                      className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
                    >
                      {createMutation.isPending ? 'Creating...' : 'Create Program'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
